import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Map, { Layer, Popup, Source } from 'react-map-gl';
import {
  createPopupContent,
  createPortGeoJson,
  createPortIcons,
  createVesselGeoJson,
  createVesselIcons
} from './utils/map';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useMapState } from './hooks/useMapState';
import { SIDE_PANEL_TYPES, VESSEL_DEFAULT_ICON_COLOR } from './constants';
import { useTimezone } from '@hooks/useTimezone';
import DashboardActivityMapZoomControls from '../../../Maps/DashboardActivityMap/DashboardActivityMapZoomControls';
import VesselsMapRouteDurationSelector from '../../../Maps/VesselsMap/VesselsMapRouteDurationSelector';
import { useTransitDetails } from '../../../Maps/VesselsMap/useTransitDetails';
import { ROUTE_LINE_STYLES, ROUTE_COLORS } from '@constants/mapConstants';

const DashboardActivityMapComponent = ({
  themeMode,
  onMapCreated,
  filteredVessels,
  filteredPorts,
  zoomLocation,
  isFleetFilterApplicable,
  isInboundFilterApplicable
}) => {
  const {
    handlePortSelect,
    handleVesselSelect,
    mapSettings,
    filterType,
    sidePanelType,
    sidePanelItemId
  } = useMapState();

  const { getTimeZoneDisplay } = useTimezone();
  const [selectedVessel, setSelectedVessel] = React.useState(null);
  const [mapInstance, setMapInstance] = React.useState(null);
  const [selectedDays, setSelectedDays] = useState(null);
  const [showRoutes, setShowRoutes] = useState(false);
  const [hoveredRouteId, setHoveredRouteId] = useState(null);

  const [enableRouteDurationSelector, setEnableRouteDurationSelector] = useState(false);

  const singleVesselViewMode = sidePanelType === SIDE_PANEL_TYPES.VESSEL_DETAILS;
  const vesselsImos = useMemo(
    () =>
      (singleVesselViewMode ? [{ imo: +sidePanelItemId }] : filteredVessels ?? []).map(
        (v) => v.imo
      ),
    [filteredVessels, singleVesselViewMode, sidePanelItemId]
  );
  const { data: transitDetails, isFetching: isLoadingTransitDetails } = useTransitDetails({
    imos: vesselsImos ?? [],
    transitDays: selectedDays
  });

  const routeGeoJson = React.useMemo(() => {
    if (!transitDetails?.length) return null;

    return {
      type: 'FeatureCollection',
      features: transitDetails
        .filter((detail) => vesselsImos.includes(detail.voyageOverview?.imo))
        .map((detail, index) => {
          const route = detail.voyageReducedTransitDetails?.transitDetails;
          if (!route?.length) return null;

          // Sort route points by timestamp
          const sortedRoute = [...route].sort(
            (a, b) => new Date(a.timeStamp).getTime() - new Date(b.timeStamp).getTime()
          );

          // Handle date line crossing
          let prevLon = sortedRoute[0].lon;
          const adjustedCoordinates = sortedRoute.map((point) => {
            let lon = point.lon;

            // Adjust longitude when crossing the date line
            while (Math.abs(lon - prevLon) > 180) {
              lon += lon < prevLon ? 360 : -360;
            }
            prevLon = lon;

            return [lon, point.lat];
          });

          return {
            type: 'Feature',
            id: index, // Add unique id for each route to support feature state
            properties: {
              color: ROUTE_COLORS[index % ROUTE_COLORS.length],
              imo: detail.voyageOverview.imo
            },
            geometry: {
              type: 'LineString',
              coordinates: adjustedCoordinates
            }
          };
        })
        .filter(Boolean)
    };
  }, [transitDetails, vesselsImos]);

  const handleDurationChange = useCallback((days) => {
    setSelectedDays(days);
    setShowRoutes(!!days);
  }, []);

  const handleMapLoad = useCallback(
    (event) => {
      const mapInstance = event.target;
      createVesselIcons(mapInstance);
      createPortIcons(mapInstance);
      onMapCreated?.(mapInstance);
      setMapInstance(mapInstance);
    },
    [onMapCreated]
  );

  const flyToLocation = useCallback(
    (longitude, latitude, zoom = 8) => {
      mapInstance?.flyTo({
        center: [longitude, latitude],
        zoom,
        transitionDuration: 2000,
        curve: 1.4
      });
    },
    [mapInstance]
  );

  useEffect(() => {
    if (zoomLocation) {
      flyToLocation(zoomLocation.lng, zoomLocation.lat, zoomLocation.zoom);
    } else {
      flyToLocation(0, 0, 1);
    }
  }, [zoomLocation?.lng, zoomLocation?.lat, zoomLocation?.zoom, flyToLocation]);

  // control the visibility of the route duration selector
  useEffect(() => {
    // Only show route duration selector if we have a manageable number of vessels or viewing a specific vessel
    if (filteredVessels.length <= 150 || sidePanelType === SIDE_PANEL_TYPES.VESSEL_DETAILS) {
      setEnableRouteDurationSelector(true);

      // Only auto-select 30 days when viewing a specific vessel's details
      if (sidePanelType === SIDE_PANEL_TYPES.VESSEL_DETAILS) {
        handleDurationChange(30);
      }
    } else {
      setEnableRouteDurationSelector(false);
    }
  }, [filteredVessels, sidePanelType, sidePanelItemId]);

  // handle the case when a single vessel is selected either on the map or in the fleet
  useEffect(() => {
    if (sidePanelType === SIDE_PANEL_TYPES.VESSEL_DETAILS) {
      const vesselPosition = filteredVessels.find((vessel) => vessel.imo === +sidePanelItemId);

      if (vesselPosition) {
        flyToLocation(vesselPosition.long, vesselPosition.lat, 4);
      } else {
        flyToLocation(0, 0, 1);
      }
    }
  }, [sidePanelType, sidePanelItemId, filteredVessels, flyToLocation]);

  const vesselGeoJson = React.useMemo(() => {
    const isFilterApplied = isFleetFilterApplicable || isInboundFilterApplicable;
    return createVesselGeoJson(filteredVessels, isFilterApplied, mapSettings.showVesselsStatus);
  }, [
    filteredVessels,
    mapSettings.showVesselsStatus,
    isFleetFilterApplicable,
    isInboundFilterApplicable
  ]);

  const portGeoJson = React.useMemo(() => {
    return createPortGeoJson(filteredPorts, filterType);
  }, [filteredPorts, filterType]);

  const onClick = (event) => {
    const features = mapInstance.queryRenderedFeatures(event.point, {
      layers: ['vessels-layer', 'ports-layer']
    });
    const feature = features?.[0];

    if (feature && feature.layer.id === 'vessels-layer') {
      const vessel = feature.properties;
      handleVesselSelect(vessel.imo);
    } else if (feature && feature.layer.id === 'ports-layer') {
      const port = feature.properties;
      handlePortSelect(port);
    } else {
      setSelectedVessel(null);
    }
  };

  const shouldShowVesselColor =
    isFleetFilterApplicable || isInboundFilterApplicable || mapSettings.showVesselsStatus;

  const handleZoomIn = useCallback(() => {
    if (mapInstance) {
      const currentZoom = mapInstance.getZoom();
      mapInstance.easeTo({
        zoom: currentZoom + 1,
        duration: 300
      });
    }
  }, [mapInstance]);

  const handleZoomOut = useCallback(() => {
    if (mapInstance) {
      const currentZoom = mapInstance.getZoom();
      mapInstance.easeTo({
        zoom: currentZoom - 1,
        duration: 300
      });
    }
  }, [mapInstance]);

  const onHover = (event) => {
    if (!mapInstance) return;

    const availableLayers = mapInstance.getStyle().layers.map((l) => l.id);
    const layersToQuery = ['vessels-layer'];

    if (availableLayers.includes('routes-layer')) {
      layersToQuery.push('routes-layer');
    }

    const features = mapInstance.queryRenderedFeatures(event.point, {
      layers: layersToQuery
    });

    const feature = features?.[0];

    if (feature) {
      if (feature.layer.id === 'vessels-layer') {
        const vessel = feature.properties;
        setSelectedVessel(vessel);

        const hoveredRouteId = routeGeoJson?.features?.find(
          (route) => route.properties.imo === vessel.imo
        )?.id;

        if (hoveredRouteId) {
          setHoveredRouteId(hoveredRouteId);
          mapInstance.setFeatureState({ source: 'routes', id: hoveredRouteId }, { hover: true });
        } else {
          if (hoveredRouteId !== null) {
            mapInstance.setFeatureState({ source: 'routes', id: hoveredRouteId }, { hover: false });
          }
          setHoveredRouteId(null);
        }

        if (routeGeoJson) {
          mapInstance.setFilter('routes-layer', ['==', ['get', 'imo'], vessel.imo]);
          mapInstance.setFilter('vessels-layer', ['==', ['get', 'imo'], vessel.imo]);
        }
      } else if (feature.layer.id === 'routes-layer') {
        const route = feature.properties;
        if (hoveredRouteId !== null) {
          mapInstance.setFeatureState({ source: 'routes', id: hoveredRouteId }, { hover: false });
        }

        const id = feature.id;
        setHoveredRouteId(id);
        mapInstance.setFeatureState({ source: 'routes', id }, { hover: true });

        mapInstance.setFilter('vessels-layer', ['==', ['get', 'imo'], route.imo]);
        mapInstance.setFilter('routes-layer', ['==', ['get', 'imo'], route.imo]);
      }
    } else {
      setSelectedVessel(null);

      if (hoveredRouteId !== null) {
        mapInstance.setFeatureState({ source: 'routes', id: hoveredRouteId }, { hover: false });
        setHoveredRouteId(null);
      }

      if (routeGeoJson) {
        mapInstance.setFilter('routes-layer', null);
        mapInstance.setFilter('vessels-layer', null);
      }
    }
  };

  const onMouseLeave = () => {
    if (hoveredRouteId !== null && mapInstance) {
      mapInstance.setFeatureState({ source: 'routes', id: hoveredRouteId }, { hover: false });
      setHoveredRouteId(null);
    }
  };

  return (
    <>
      <Map
        key={`activity-map-${themeMode}`}
        onLoad={handleMapLoad}
        mapStyle={`mapbox://styles/mapbox/${themeMode ? 'dark' : 'light'}-v11`}
        interactiveLayerIds={['vessels-layer', 'ports-layer', 'routes-layer']}
        cursor="pointer"
        projection="mercator"
        onClick={onClick}
        onMouseMove={onHover}
        onMouseLeave={onMouseLeave}
        initialViewState={{
          longitude: -100,
          latitude: 40,
          zoom: 1.5
        }}>
        <Source
          id="vessels"
          type="geojson"
          data={
            mapSettings.showVessels ? vesselGeoJson : { type: 'FeatureCollection', features: [] }
          }
          generateId={true}>
          <Layer
            id="vessels-layer"
            type="symbol"
            interactive={true}
            layout={{
              'icon-image': ['get', 'iconImage'],
              'icon-size': 0.5,
              'icon-rotate': ['get', 'rotation'],
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'text-field': mapSettings.showVesselsNames ? ['get', 'vesselName'] : '',
              'text-anchor': 'top',
              'text-offset': [0, 1],
              'text-size': 10
            }}
            paint={{
              'icon-color': [
                'case',
                ['has', 'color'],
                ['get', 'color'],
                shouldShowVesselColor ? ['get', 'color'] : VESSEL_DEFAULT_ICON_COLOR
              ],
              'text-color': themeMode ? 'white' : 'black'
            }}
          />
        </Source>

        <Source
          id="ports"
          type="geojson"
          data={
            mapSettings.showPorts
              ? portGeoJson
              : {
                  type: 'FeatureCollection',
                  features: []
                }
          }
          generateId={true}>
          <Layer
            id="ports-layer"
            type="symbol"
            layout={{
              'icon-image': ['get', 'iconImage'],
              'icon-size': 1,
              'icon-allow-overlap': true,
              'text-field': mapSettings.showPortNames ? ['get', 'portName'] : '',
              'text-anchor': 'top',
              'text-offset': [0, 1],
              'text-size': 12
            }}
            paint={{
              'icon-color': ['get', 'color'],
              'text-color': themeMode ? 'white' : 'black'
            }}
          />
        </Source>

        {routeGeoJson && selectedDays && showRoutes && (
          <Source id="routes" type="geojson" data={routeGeoJson}>
            <Layer
              id="routes-layer"
              type="line"
              paint={{
                'line-color': ['get', 'color'],
                'line-width': [
                  'case',
                  ['boolean', ['feature-state', 'hover'], false],
                  ROUTE_LINE_STYLES.DEFAULT.WIDTH * 2,
                  ROUTE_LINE_STYLES.DEFAULT.WIDTH
                ],
                'line-opacity': ROUTE_LINE_STYLES.DEFAULT.OPACITY
              }}
              layout={{
                'line-join': 'round',
                'line-cap': 'round'
              }}
            />
          </Source>
        )}

        {selectedVessel && (
          <Popup
            longitude={selectedVessel.long}
            latitude={selectedVessel.lat}
            anchor="top"
            className={`popup-details ${themeMode ? 'popup-dark' : 'popup-light'}`}
            onClose={() => setSelectedVessel(null)}
            closeButton={false}
            closeOnClick={false}>
            {createPopupContent(selectedVessel, themeMode, getTimeZoneDisplay)}
          </Popup>
        )}
      </Map>
      <DashboardActivityMapZoomControls onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} />
      <VesselsMapRouteDurationSelector
        selectedDays={selectedDays}
        onDurationChange={handleDurationChange}
        isLoading={isLoadingTransitDetails}
        enabled={enableRouteDurationSelector}
      />
    </>
  );
};

DashboardActivityMapComponent.propTypes = {
  zoomLocation: PropTypes.object,
  themeMode: PropTypes.bool,
  onMapCreated: PropTypes.func,
  filteredVessels: PropTypes.array,
  filteredPorts: PropTypes.array,
  isFleetFilterApplicable: PropTypes.bool,
  isInboundFilterApplicable: PropTypes.bool
};

export default DashboardActivityMapComponent;
